import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { authenticationRequiredEvent, clearStoredAuthentication, hasCSRFToken, passwordChangeRequiredEvent } from '../shared/api/client'
import { authenticatedHome, hasWorkspaceCapabilities, restoreAccount } from '../shared/auth/session'
import { setupStatus } from '../setup/api'

const OperationsOverviewPage = () => import('../admin/pages/OperationsOverviewPage.vue')

const adminChildren: RouteRecordRaw[] = [
  { path: '', component: OperationsOverviewPage, meta: { title: '运行总览', section: '工作台', requiresAuth: true } },
  { path: 'tasks', component: OperationsOverviewPage, meta: { title: '待办中心', section: '工作台', requiresAuth: true } },
  { path: 'components', component: OperationsOverviewPage, meta: { title: '组件状态', section: '工作台', requiresAuth: true } },
  { path: 'organizations', component: () => import('../admin/pages/OrganizationsPage.vue'), meta: { title: '组织管理', section: '组织与域名', requiresAuth: true } },
  { path: 'domains', component: () => import('../admin/pages/DomainsPage.vue'), meta: { title: '主域与域别名', section: '组织与域名', requiresAuth: true } },
  { path: 'dns', component: () => import('../admin/pages/DomainDNSPage.vue'), meta: { title: 'DNS 与 DKIM', section: '组织与域名', requiresAuth: true } },
  { path: 'mailboxes', component: () => import('../admin/pages/MailboxesPage.vue'), meta: { title: '邮箱账号', section: '邮箱账号', requiresAuth: true } },
  { path: 'mailbox-imports', component: () => import('../admin/pages/MailboxImportsPage.vue'), meta: { title: '批量导入', section: '邮箱账号', requiresAuth: true } },
  { path: 'mailbox-trash', component: () => import('../admin/pages/MailboxesPage.vue'), meta: { title: '邮箱回收站', section: '邮箱账号', requiresAuth: true } },
  { path: 'aliases', component: () => import('../admin/pages/AliasesPage.vue'), meta: { title: '地址别名', section: '路由与群组', requiresAuth: true } },
  { path: 'catch-all', component: () => import('../admin/pages/CatchAllPage.vue'), meta: { title: '全域收件与系统别名', section: '路由与群组', requiresAuth: true } },
  { path: 'groups', component: () => import('../admin/pages/GroupsPage.vue'), meta: { title: '邮件组', section: '路由与群组', requiresAuth: true } },
  { path: 'quarantine', component: () => import('../admin/pages/MailSecurityPage.vue'), meta: { title: '隔离区', section: '邮件安全', requiresAuth: true } },
  { path: 'allow-block', component: () => import('../admin/pages/MailSecurityPage.vue'), meta: { title: '允许与阻止', section: '邮件安全', requiresAuth: true } },
  { path: 'antispam', component: () => import('../admin/pages/MailSecurityPage.vue'), meta: { title: '反垃圾统计', section: '邮件安全', requiresAuth: true } },
  { path: 'queue', component: () => import('../admin/pages/DeliveryOperationsPage.vue'), meta: { title: '邮件队列', section: '队列与投递', requiresAuth: true } },
  { path: 'delivery', component: () => import('../admin/pages/DeliveryOperationsPage.vue'), meta: { title: '投递追踪', section: '队列与投递', requiresAuth: true } },
  { path: 'bounces', component: () => import('../admin/pages/BounceAnalyticsPage.vue'), meta: { title: '退信分析', section: '队列与投递', requiresAuth: true } },
  { path: 'logs', component: () => import('../admin/pages/LogsPage.vue'), meta: { title: '日志中心', section: '日志与审计', requiresAuth: true } },
  { path: 'audit', component: () => import('../admin/pages/SecurityEventsPage.vue'), meta: { title: '审计日志', section: '日志与审计', requiresAuth: true } },
  { path: 'auth-events', component: () => import('../admin/pages/SecurityEventsPage.vue'), meta: { title: '安全事件', section: '日志与审计', requiresAuth: true } },
  {
    path: 'mail-config',
    component: () => import('../admin/pages/MailConfigurationPage.vue'),
    meta: { title: '邮件配置', section: '配置与系统', requiresAuth: true },
  },
  { path: 'config-versions', component: () => import('../admin/pages/MailConfigurationPage.vue'), props: { initialTab: 'versions' }, meta: { title: '配置版本', section: '配置与系统', requiresAuth: true } },
  { path: 'jobs', component: OperationsOverviewPage, meta: { title: '任务中心', section: '配置与系统', requiresAuth: true } },
  { path: 'alerts', component: () => import('../admin/pages/AlertCenterPage.vue'), meta: { title: '告警中心', section: '配置与系统', requiresAuth: true } },
  { path: 'backups', component: () => import('../admin/pages/BackupsPage.vue'), meta: { title: '备份与恢复', section: '配置与系统', requiresAuth: true } },
]

const mailChildren: RouteRecordRaw[] = [
  { path: '', redirect: '/mail/inbox' },
  { path: 'compose', component: () => import('../mail/pages/ComposePage.vue'), meta: { title: '写邮件', requiresAuth: true } },
  { path: 'contacts', component: () => import('../mail/pages/ContactsPage.vue'), meta: { title: '通讯录', requiresAuth: true } },
  { path: 'templates', component: () => import('../mail/pages/TemplatesPage.vue'), meta: { title: '邮件模板', requiresAuth: true } },
  { path: 'quarantine', component: () => import('../mail/pages/QuarantinePage.vue'), meta: { title: '隔离区', requiresAuth: true } },
  { path: 'rules', component: () => import('../mail/pages/RulesPage.vue'), meta: { title: '过滤规则', requiresAuth: true } },
  { path: 'vacation', component: () => import('../mail/pages/VacationPage.vue'), meta: { title: '自动回复', requiresAuth: true } },
  { path: ':folder', component: () => import('../mail/pages/MailboxPage.vue'), meta: { title: '邮箱', requiresAuth: true } },
]

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', redirect: '/login' },
    { path: '/setup', component: () => import('../setup/SetupPage.vue'), meta: { public: true } },
    { path: '/login', component: () => import('../auth/LoginPage.vue'), meta: { public: true } },
    { path: '/password-reset', component: () => import('../auth/PasswordResetPage.vue'), meta: { public: true } },
    { path: '/approvals', component: () => import('../approvals/MailConfigApprovalPage.vue'), meta: { requiresAuth: true, title: '配置审批' } },
    {
      path: '/admin',
      component: () => import('../admin/AdminLayout.vue'),
      children: adminChildren,
      meta: { requiresAuth: true },
    },
    {
      path: '/mail',
      component: () => import('../mail/MailLayout.vue'),
      children: mailChildren,
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      component: () => import('../settings/SettingsPage.vue'),
      meta: { requiresAuth: true, title: '个人设置' },
    },
    {
      path: '/:pathMatch(.*)*',
      component: () => import('../shared/pages/NotFoundPage.vue'),
      meta: { public: true },
    },
  ],
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  try {
    const setup = await setupStatus()
    if (setup.required && to.path !== '/setup') return { path: '/setup' }
    if (!setup.required && to.path === '/setup') return { path: '/login' }
  } catch {
    if (to.path === '/setup') return true
  }
  if (to.meta.requiresAuth && !hasCSRFToken()) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  const account = restoreAccount()
  const passwordChangeRequired = hasCSRFToken() && account?.mustChangePassword === true
  if (passwordChangeRequired && to.path !== '/settings') return { path: '/settings', query: { password: 'required' } }
  if (to.path === '/login' && hasCSRFToken()) {
    if (!hasWorkspaceCapabilities(account)) {
      clearStoredAuthentication()
      return true
    }
    return authenticatedHome(account)
  }
  if (hasWorkspaceCapabilities(account) && (to.path === '/mail' || to.path.startsWith('/mail/')) && !account.canAccessWebmail) {
    return authenticatedHome(account)
  }
  if (hasWorkspaceCapabilities(account) && (to.path === '/admin' || to.path.startsWith('/admin/')) && !account.canAccessAdmin) {
    return authenticatedHome(account)
  }
  if (hasWorkspaceCapabilities(account) && to.path === '/approvals' && !account.canApproveMailConfig) {
    return authenticatedHome(account)
  }
  return true
})

window.addEventListener(authenticationRequiredEvent, () => {
  const current = router.currentRoute.value
  clearStoredAuthentication()
  if (current.meta.public || current.path === '/login') return
  void router.replace({ path: '/login', query: { redirect: current.fullPath } })
})

window.addEventListener(passwordChangeRequiredEvent, () => {
  if (router.currentRoute.value.path === '/settings') return
  void router.replace({ path: '/settings', query: { password: 'required' } })
})
